import { request } from '@/fetch/core';
import { Method } from '@/fetch/core/constant';

export interface VehicleDemandPageRequest {
  startTime?: string;
  endTime?: string;
  provinceId?: number | null;
  cityId?: number | null;
  countryId?: number | null;
  provinceAgencyCode?: string;
  areaCode?: string;
  stationNumber?: string;
  vehicleModelType?: string;
  stationUseCase?: string;
  status?: string;
  requirementNumber?: string;
  pageNum: number;
  pageSize: number;
}

export interface AddVehicleDemandRequest {
  vehicleModelType: string;
  count: number;
  contact: string;
  contactErp?: string;
  contactPhone: string;
  mail: string;
  alternateContact?: string;
  alternateErp?: string;
  alternateContactPhone?: string;
  stationName: string;
  stationUseCase: string;
  stationNumber: string;
  provinceId: number;
  cityId: number;
  countryId: number;
  address: string;
}

export interface EditVehicleDemandRequest {
  requirementId: number;
  version: number;
  currentData: {
    stationName: string;
    stationNumber: string;
    contact: string;
    contactErp: string;
    contactPhone: string;
    mail?: string;
    address: string;
    stationUseCase: string;
    alternateContact?: string;
    alternateContactPhone?: string;
    count: number;
    vehicleModelType: string;
    expectedDeliveryMonth?: string;
    provinceId: number;
    cityId: number;
    countryId: number;
  };
}

export interface SubmitOrderRequest {
  submitOrderList: Array<{
    requirementId: number;
    expectedDeliveryMonth: string;
  }>;
}

export interface DeleteRequirementRequest {
  requirementId: number;
}

export interface RejectRequirementRequest {
  requirementIdList: number[];
  rejectReason?: string;
}

export interface ResubmitRequirementRequest {
  requirementId: number;
}

export interface UpdateLogRequest {
  requirementNumber: string;
  pageNum: number;
  pageSize: number;
}

export interface BatchSubmitRequest {
  bucketName: string;
  fileKey: string;
}

export interface GetDownloadUrlRequest {
  type: number;
}

export interface RoadAcceptRequest {
  acceptList: number[];
}

export enum permissionApi {
  ALL = '/k2/management/deployment/requirement/page',
  NEOLIX = '/k2/management/deployment/requirement/openPage',
}

export const vehicleDemandPageApiInfo = [
  {
    code: permissionApi.ALL,
    functionName: 'getVehicleDemandPage',
  },
  {
    code: permissionApi.NEOLIX,
    functionName: 'getNeolixVehicleDemandPage',
  },
];

class VehicleDemandManageApi {
  // 分页查询用车需求-全量查询
  public async getVehicleDemandPage(
    params: VehicleDemandPageRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: permissionApi.ALL,
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 分页查询用车需求-新石器查询
  public async getNeolixVehicleDemandPage(
    params: VehicleDemandPageRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: permissionApi.NEOLIX,
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 需求详情查询
  public async getVehicleDemandDetail(requirementId: number): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/getDetailById',
      method: Method.POST,
      body: { requirementId },
    };
    return request(requestOptions);
  }

  // 新增用车需求
  public async addVehicleDemand(params: AddVehicleDemandRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/add',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 编辑用车需求
  public async editVehicleDemand(
    params: EditVehicleDemandRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/edit',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 需求下单
  public async submitOrder(params: SubmitOrderRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/submitOrder',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 删除需求
  public async deleteRequirement(
    params: DeleteRequirementRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/deleteById',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 需求驳回或路权审核驳回
  public async rejectRequirement(
    params: RejectRequirementRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/reject',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 需求重新提交
  public async resubmitRequirement(
    params: ResubmitRequirementRequest,
  ): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/resubmit',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 获取修改记录
  public async getUpdateLog(params: UpdateLogRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/pageUpdateLog',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 批量上传用车需求
  public async batchSubmit(params: BatchSubmitRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/batchAdd',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 获取批量需求模板下载链接
  public async getDownloadUrl(params: GetDownloadUrlRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/upload/get_download_url',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }

  // 获取历史需求记录
  public async getBeforeData(): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/getBeforeData',
      method: Method.POST,
    };
    return request(requestOptions);
  }

  // 路权审核通过
  public async roadAccept(params: RoadAcceptRequest): Promise<any> {
    const requestOptions: RequestOptions = {
      path: '/k2/management/deployment/requirement/roadAccept',
      method: Method.POST,
      body: params,
    };
    return request(requestOptions);
  }
}

export const vehicleDemandManageApi = new VehicleDemandManageApi();
